import { request, type ApiFetch, type ApiResult } from './client.ts'

// Typed client for the webhook read API (INT-367).
//
// The response types are redeclared here rather than imported. The service's zod
// schemas are module-private, and the hono RPC `Client` type is unreachable in
// the other direction: `@curri/integrations-service` already depends on this
// package to serve the bundle, so importing back is a Rush cycle. The shapes
// below are transcribed from `surfaces/rest/webhooks.ts`; the OpenAPI document
// at /openapi.json is the contract both sides answer to.

export type WebhookVariant =
  | 'deliveries'
  | 'sandboxDeliveries'
  | 'payloads'
  | 'sandboxPayloads'

/** One effective endpoint — only URLs that would really fire are returned. */
export interface WebhookEndpointConfig {
  variant: WebhookVariant
  url: string
  /** Null whenever the URL came from the legacy account column. */
  authHeaderName: string | null
  /** Only the payload variants sign; the delivery variants are never true. */
  hasSigningSecret: boolean
  source: 'integrationSettings' | 'legacyColumn'
}

export interface WebhookAttempt {
  id: number
  deliveryId: number | null
  deliveryExternalId: string | null
  sentAt: string | null
  success: boolean
  /** Null when the attempt never got a reply — a timeout or a reset. */
  httpStatus: number | null
  statusText: string | null
  isSandbox: boolean
  /** The delivery status the body carried (`payload.status`), lifted out by the
   *  service. Null when the stored body has none. */
  deliveryStatus: string | null
  /** Arbitrary JSON: the body Curri POSTed. May be null. */
  payload: unknown
}

export interface WebhookAttemptPage {
  attempts: WebhookAttempt[]
  nextCursor: number | null
}

export interface WebhookSummary {
  windowDays: number
  delivered: number
  failed: number
  /** Null — not zero — when nothing was sent in the window. */
  successRate: number | null
  /** A current count, not windowed: retry state carries no timestamp. */
  deadDeliveries: number
}

export type AttemptStatusFilter = 'all' | 'delivered' | 'failed'

/**
 * What the service accepts as a `deliveryExternalId` filter — the same pattern
 * `attemptsQuerySchema` enforces in surfaces/rest/webhooks.ts. Checked here so
 * a half-typed id is held back rather than sent to be refused with a 400.
 */
export const DELIVERY_EXTERNAL_ID_PATTERN = /^del_[A-Za-z0-9]+$/

export const WEBHOOK_READ_SCOPE = 'webhook:read'
/**
 * Gates both dashboard mutations: resending an attempt and editing an endpoint.
 * Without it the screen renders, but read-only.
 */
export const WEBHOOK_WRITE_SCOPE = 'webhook:write'

/** The 202 body of a queued redelivery. */
export interface WebhookResendReceipt {
  deliveryId: number
  deliveryExternalId: string | null
}

// The API rejects an out-of-range window rather than clamping it, so the UI must
// not offer a range it would refuse. Temporarily 7 while the attempts table has
// no per-user index — see MAX_WINDOW_DAYS in the service's surfaces/rest/webhooks.ts.
export const MAX_WINDOW_DAYS = 7
export const PAGE_SIZE = 25

/**
 * The shared request helper, bound to the scope a 403 on *this* call most
 * plausibly means when a proxy strips the challenge header naming it.
 *
 * The reads and the writes need different answers: telling someone whose save
 * was refused that they lack `webhook:read` sends them to re-request a scope
 * they already hold, and never names the one they need.
 */
const webhookRead = <T>(
  apiFetch: ApiFetch,
  path: string,
  init: RequestInit
): Promise<ApiResult<T>> => request(apiFetch, path, init, WEBHOOK_READ_SCOPE)

const webhookWrite = <T>(
  apiFetch: ApiFetch,
  path: string,
  init: RequestInit
): Promise<ApiResult<T>> => request(apiFetch, path, init, WEBHOOK_WRITE_SCOPE)

export const fetchWebhookConfig = (
  apiFetch: ApiFetch,
  signal: AbortSignal
): Promise<ApiResult<WebhookEndpointConfig[]>> =>
  webhookRead(apiFetch, '/api/webhooks/config', { signal })

export const fetchWebhookSummary = (
  apiFetch: ApiFetch,
  windowDays: number,
  signal: AbortSignal
): Promise<ApiResult<WebhookSummary>> =>
  webhookRead(apiFetch, `/api/webhooks/summary?windowDays=${windowDays}`, {
    signal,
  })

/**
 * Queue a redelivery for the delivery behind an attempt. No abort signal: once
 * the user has clicked, an unmounting row must not turn a queued send into a
 * reported failure — the request is fire-and-report.
 */
export const resendWebhookAttempt = (
  apiFetch: ApiFetch,
  attemptId: number
): Promise<ApiResult<WebhookResendReceipt>> =>
  webhookWrite(apiFetch, `/api/webhooks/attempts/${attemptId}/resend`, {
    method: 'POST',
  })

/** The config PUT returns, plus the one-time secret when it minted or rotated one. */
export interface UpdatedWebhookEndpointConfig extends WebhookEndpointConfig {
  /**
   * Present only on the first write to a payload variant that has no secret
   * yet, or on a write sending `rotateSigningSecret: true`. No read ever
   * returns it, so store it when it arrives.
   */
  signingSecret?: string
}

/**
 * Change a variant's webhook URL, its auth header, or both. Returns the
 * now-effective config for that variant.
 *
 * Send only what changed: omitting `url` keeps the stored URL, and omitting
 * `authHeader` keeps the stored header — which is what lets someone fix a URL
 * without knowing the credential, since Curri never gives the value back.
 * `authHeader: null` removes it.
 *
 * The payload variants sign. Configuring one for the first time mints a
 * signing secret and returns it once on `signingSecret`; send
 * `rotateSigningSecret: true` to replace it, which invalidates the old one
 * immediately. The delivery variants are never signed.
 *
 * No abort signal, for the same reason the resend above has none: once the user
 * has saved, an unmounting form must not turn a write that already landed into a
 * reported failure.
 *
 * Always through the auth provider's own fetch, never the ghost fetch: the
 * server rejects a ghosted write (`Curri-As-User` is read-only), and the editor
 * is hidden while ghosting anyway.
 */
export const putWebhookEndpoint = (
  apiFetch: ApiFetch,
  body: {
    variant: WebhookVariant
    url?: string
    authHeader?: { name: string; value: string } | null
    rotateSigningSecret?: boolean
  }
): Promise<ApiResult<UpdatedWebhookEndpointConfig>> =>
  webhookWrite(apiFetch, '/api/webhooks/config', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })

/** The result of POST /api/webhooks/test — what the sandbox endpoint answered. */
export interface WebhookTestSendResult {
  url: string
  sentAt: string
  durationMs: number
  /** 2xx from the endpoint. A failing endpoint is still an ok ApiResult —
   *  the test happened, and this reports its outcome. */
  success: boolean
  /** Null when the endpoint never replied — a timeout has no HTTP status. */
  httpStatus: number | null
  statusText: string | null
  /** The first bytes of the endpoint's response body, truncated server-side. */
  responseBody: string | null
  transportError: string | null
}

/**
 * POST a webhook-shaped payload at the caller's configured **sandbox**
 * endpoint and report what it answered. The service validates the shape,
 * forces `isSandbox: true`, and never touches the production endpoint.
 *
 * No abort signal, like the other mutations: once the user has clicked send,
 * an unmounting panel must not turn a request that already landed on their
 * server into a reported failure. And always through the auth provider's own
 * fetch — ghost sessions are read-only server-side, and the send controls are
 * disabled while ghosting anyway.
 */
export const sendTestWebhook = (
  apiFetch: ApiFetch,
  payload: Record<string, unknown>
): Promise<ApiResult<WebhookTestSendResult>> =>
  webhookWrite(apiFetch, '/api/webhooks/test', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ payload }),
  })

/**
 * Every recorded attempt for one delivery, by its partner-visible `del_*` id —
 * the bookings dashboard's per-row "webhooks sent" read. Pinned to a single
 * delivery the service widens the window past the dashboard's 7-day cap, so
 * an old booking still shows its history.
 */
export const fetchAttemptsForDelivery = (
  apiFetch: ApiFetch,
  deliveryExternalId: string,
  signal: AbortSignal
): Promise<ApiResult<WebhookAttemptPage>> => {
  const query = new URLSearchParams({
    deliveryExternalId,
    limit: String(PAGE_SIZE),
  })
  return webhookRead(apiFetch, `/api/webhooks/attempts?${query.toString()}`, {
    signal,
  })
}

/**
 * A page of the dashboard's attempts table. `deliveryExternalId` pins the page
 * to one delivery, and the service then widens the window past `windowDays` to
 * the full retained history — the same read `fetchAttemptsForDelivery` makes,
 * with the outcome filter and the cursor kept.
 */
export const fetchWebhookAttempts = (
  apiFetch: ApiFetch,
  params: {
    windowDays: number
    status: AttemptStatusFilter
    before?: number
    deliveryExternalId?: string
  },
  signal: AbortSignal
): Promise<ApiResult<WebhookAttemptPage>> => {
  const query = new URLSearchParams({
    windowDays: String(params.windowDays),
    limit: String(PAGE_SIZE),
  })
  if (params.status !== 'all') query.set('status', params.status)
  if (params.before !== undefined) query.set('before', String(params.before))
  if (params.deliveryExternalId !== undefined) {
    query.set('deliveryExternalId', params.deliveryExternalId)
  }

  return webhookRead(apiFetch, `/api/webhooks/attempts?${query.toString()}`, {
    signal,
  })
}

// ---- Payload webhooks ----
// A payload attempt carries the event that caused it, the envelope that was
// POSTed, and its place on that event's retry ladder — none of which a
// delivery attempt has, so these are their own types and endpoints rather than
// a reuse of the ones above.

export interface PayloadWebhookAttempt {
  id: number
  eventId: string
  eventType: string
  eventCreatedAt: string
  attemptNumber: number
  payloadExternalId: string | null
  sentAt: string
  success: boolean
  httpStatus: number | null
  durationMs: number
  isSandbox: boolean
  responseBody: string | null
  requestHeaders: Record<string, string>
  body: unknown
}

export interface PayloadWebhookAttemptPage {
  attempts: PayloadWebhookAttempt[]
  nextCursor: number | null
}

export interface PayloadWebhookSummary {
  windowDays: number
  delivered: number
  failed: number
  /** Null — not zero — when nothing was sent in the window. */
  successRate: number | null
}

export const fetchPayloadWebhookSummary = (
  apiFetch: ApiFetch,
  windowDays: number,
  signal: AbortSignal
): Promise<ApiResult<PayloadWebhookSummary>> =>
  webhookRead(
    apiFetch,
    `/api/webhooks/payloads/summary?windowDays=${windowDays}`,
    { signal }
  )

export const fetchPayloadWebhookAttempts = (
  apiFetch: ApiFetch,
  params: {
    windowDays: number
    status: AttemptStatusFilter
    before?: number
    eventType?: string
  },
  signal: AbortSignal
): Promise<ApiResult<PayloadWebhookAttemptPage>> => {
  const query = new URLSearchParams({
    windowDays: String(params.windowDays),
    limit: String(PAGE_SIZE),
  })
  if (params.status !== 'all') query.set('status', params.status)
  if (params.before !== undefined) query.set('before', String(params.before))
  if (params.eventType !== undefined) query.set('eventType', params.eventType)

  return webhookRead(
    apiFetch,
    `/api/webhooks/payloads/attempts?${query.toString()}`,
    { signal }
  )
}

/**
 * Every recorded attempt for one payload, by its partner-visible `payload_*`
 * id. Pinned to a single payload the service widens the window past the
 * dashboard's 7-day cap, so an old payload still shows its history.
 */
export const fetchAttemptsForPayload = (
  apiFetch: ApiFetch,
  payloadExternalId: string,
  signal: AbortSignal
): Promise<ApiResult<PayloadWebhookAttemptPage>> => {
  const query = new URLSearchParams({
    payloadExternalId,
    limit: String(PAGE_SIZE),
  })
  return webhookRead(
    apiFetch,
    `/api/webhooks/payloads/attempts?${query.toString()}`,
    { signal }
  )
}

/** The result of POST /api/webhooks/payloads/test — what the sandbox endpoint answered. */
export interface PayloadWebhookTestSendResult {
  url: string
  sentAt: string
  durationMs: number
  /** 2xx from the endpoint. A failing endpoint is still an ok ApiResult —
   *  the test happened, and this reports its outcome. */
  success: boolean
  /** Null when the endpoint never replied — a timeout has no HTTP status. */
  httpStatus: number | null
  /** The first bytes of the endpoint's response body, truncated server-side. */
  responseBody: string | null
  transportError: string | null
  eventId: string
  eventType: string
  headers: Record<string, string>
}

/**
 * POST a payload-shaped event at the caller's configured **sandbox** payload
 * endpoint and report what it answered. The service builds the envelope,
 * signs it with the endpoint's own secret, and never touches the production
 * endpoint.
 *
 * No abort signal, like the other mutations: once the user has clicked send,
 * an unmounting panel must not turn a request that already landed on their
 * server into a reported failure. And always through the auth provider's own
 * fetch — ghost sessions are read-only server-side, and the send controls are
 * disabled while ghosting anyway.
 */
export const sendTestPayloadWebhook = (
  apiFetch: ApiFetch,
  body: { type: string; payload: Record<string, unknown> }
): Promise<ApiResult<PayloadWebhookTestSendResult>> =>
  webhookWrite(apiFetch, '/api/webhooks/payloads/test', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ type: body.type, payload: body.payload }),
  })
